/**
 * Authentication Service
 *
 * Centralized service for handling all authentication-related operations.
 * NOW INTEGRATED WITH REAL BACKEND API!
 *
 * SECURITY FEATURES:
 * - JWT token-based authentication
 * - Secure token storage (sessionStorage/localStorage)
 * - Automatic token refresh mechanism
 * - Secure logout (token invalidation)
 * - XSS and CSRF protection
 * - Rate limiting handled by backend
 * - Role-based permissions
 *
 * @module authService
 */

import { apiClient } from '@api/client'
import { config } from '@config'

/**
 * Storage keys for authentication data
 */
const STORAGE_KEYS = {
	ACCESS_TOKEN: 'auth_access_token',
	REFRESH_TOKEN: 'auth_refresh_token',
	USER_DATA: 'auth_user_data',
	TOKEN_EXPIRY: 'auth_token_expiry',
	REMEMBER_EMAIL: 'auth_remember_email'
}

/**
 * Token configuration (matches backend)
 */
const TOKEN_CONFIG = {
	// Token validity duration (in milliseconds)
	ACCESS_TOKEN_LIFETIME: 15 * 60 * 1000, // 15 minutes
	REFRESH_TOKEN_LIFETIME: 7 * 24 * 60 * 60 * 1000, // 7 days
	// Time before expiry to trigger refresh (in milliseconds)
	REFRESH_THRESHOLD: 5 * 60 * 1000 // 5 minutes before expiry
}

/**
 * Always use localStorage so auth data is shared across browser tabs.
 * Token expiry enforces the session lifetime regardless of storage location.
 */
const getStorage = () => localStorage

/**
 * Authentication Service Class
 */
class AuthService {
	constructor() {
		this.isAuthenticated = false
		this.currentUser = null
		this.refreshTokenTimer = null
		this.init()
	}

	/**
	 * Initialize authentication service
	 */
	init() {
		const token = this.getAccessToken()
		if (token) {
			if (this.isTokenValid()) {
				this.isAuthenticated = true
				this.currentUser = this.getUserData()
				apiClient.setAuthToken(token)
				this.setupTokenRefresh()
			} else {
				// Token expired, attempt refresh
				this.refreshAccessToken()
			}
		}
	}

	// ============================================================================
	// AUTHENTICATION METHODS
	// ============================================================================

	/**
	 * Login user with email and password
	 *
	 * @param {Object} credentials - Login credentials
	 * @param {string} credentials.email - User email
	 * @param {string} credentials.password - User password
	 * @param {boolean} credentials.rememberMe - Whether to persist session
	 * @returns {Promise<Object>} User data and tokens
	 * @throws {Error} If authentication fails
	 */
	async login({ email, password, rememberMe = false }) {
		try {
			// Input sanitization
			const sanitizedEmail = email.toLowerCase().trim()

			// Call REAL backend API
			const response = await apiClient.postForm('/auth/login', {
				email: sanitizedEmail,
				password: password,
				rememberMe: rememberMe
			})

			// Backend response structure:
			// {
			//   success: true,
			//   message: "Login successful",
			//   data: {
			//     user: { id, email, first_name, last_name, company_id, role: {...} },
			//     access_token: "...",
			//     refresh_token: "...",
			//     token_type: "Bearer",
			//     expires_in: 900
			//   }
			// }

			if (!response.success || !response.data) {
				throw new Error(response.message || 'Login failed')
			}

			const { user, access_token, refresh_token, expires_in } = response.data

			// Store authentication data
			this.setAccessToken(access_token, rememberMe)
			this.setRefreshToken(refresh_token, rememberMe)
			this.setUserData(user, rememberMe)

			// Set token expiry time
			const expiryTime = Date.now() + (expires_in * 1000)
			this.setTokenExpiry(expiryTime, rememberMe)

			// Update service state
			this.isAuthenticated = true
			this.currentUser = user

			// Configure API client with auth token
			apiClient.setAuthToken(access_token)

			// Setup automatic token refresh
			this.setupTokenRefresh()

			// Handle remember me
			if (rememberMe) {
				this.setRememberedEmail(sanitizedEmail)
			} else {
				this.clearRememberedEmail()
			}

			return { user, access_token }
		} catch (error) {
			console.error('Login failed:', error.message)
			throw this.handleAuthError(error)
		}
	}

	/**
	 * Logout user
	 *
	 * @param {boolean} allDevices - Whether to logout from all devices
	 * @returns {Promise<void>}
	 */
	async logout(allDevices = false) {
		try {
			const refreshToken = this.getRefreshToken()

			// Notify backend to invalidate tokens
			if (refreshToken) {
				await apiClient.postForm('/auth/logout', {
					refresh_token: refreshToken,
					all_devices: allDevices
				}).catch(err => {
					console.warn('Backend logout failed:', err.message)
				})
			}

			// Clear local authentication data
			this.clearAuthData()

			// Update service state
			this.isAuthenticated = false
			this.currentUser = null

			// Remove auth token from API client
			apiClient.setAuthToken(null)

			// Clear token refresh timer
			if (this.refreshTokenTimer) {
				clearTimeout(this.refreshTokenTimer)
				this.refreshTokenTimer = null
			}
		} catch (error) {
			// Even if backend call fails, clear local data
			this.clearAuthData()
			console.error('Logout error:', error.message)
		}
	}

	/**
	 * Refresh access token using refresh token
	 *
	 * @returns {Promise<string>} New access token
	 * @throws {Error} If refresh fails
	 */
	async refreshAccessToken() {
		try {
			const refreshToken = this.getRefreshToken()

			if (!refreshToken) {
				throw new Error('No refresh token available')
			}

			// Call backend refresh endpoint
			const response = await apiClient.postForm('/auth/refresh', {
				refresh_token: refreshToken
			})

			if (!response.success || !response.data) {
				throw new Error(response.message || 'Token refresh failed')
			}

			const { access_token, refresh_token: newRefreshToken, expires_in } = response.data

			// Update tokens
			const rememberMe = this.isTokenPersistent()
			this.setAccessToken(access_token, rememberMe)

			if (newRefreshToken) {
				this.setRefreshToken(newRefreshToken, rememberMe)
			}

			// Update expiry time
			const expiryTime = Date.now() + (expires_in * 1000)
			this.setTokenExpiry(expiryTime, rememberMe)

			// Update API client
			apiClient.setAuthToken(access_token)

			// Setup next refresh
			this.setupTokenRefresh()

			return access_token
		} catch (error) {
			console.error('Token refresh failed:', error.message)
			// If refresh fails, logout user
			await this.logout()
			throw new Error('Session expired. Please login again.')
		}
	}

	/**
	 * Get current user from backend
	 *
	 * @returns {Promise<Object>} User data
	 */
	async fetchCurrentUser() {
		try {
			const response = await apiClient.get('/auth/me')

			if (!response.success || !response.data) {
				throw new Error('Failed to fetch user data')
			}

			// Update stored user data
			const rememberMe = this.isTokenPersistent()
			this.setUserData(response.data, rememberMe)
			this.currentUser = response.data

			return response.data
		} catch (error) {
			console.error('Fetch user failed:', error.message)
			throw this.handleAuthError(error)
		}
	}

	// ============================================================================
	// TOKEN MANAGEMENT
	// ============================================================================

	/**
	 * Setup automatic token refresh
	 */
	setupTokenRefresh() {
		// Clear existing timer
		if (this.refreshTokenTimer) {
			clearTimeout(this.refreshTokenTimer)
		}

		const expiryTime = this.getTokenExpiry()
		if (!expiryTime) return

		const currentTime = Date.now()
		const timeUntilRefresh = expiryTime - currentTime - TOKEN_CONFIG.REFRESH_THRESHOLD

		if (timeUntilRefresh > 0) {
			this.refreshTokenTimer = setTimeout(() => {
				this.refreshAccessToken()
			}, timeUntilRefresh)
		} else {
			// Token already expired or about to expire, refresh immediately
			this.refreshAccessToken()
		}
	}

	/**
	 * Check if current access token is valid
	 */
	isTokenValid() {
		const token = this.getAccessToken()
		const expiryTime = this.getTokenExpiry()

		if (!token || !expiryTime) return false

		return Date.now() < expiryTime
	}

	// ============================================================================
	// STORAGE METHODS
	// ============================================================================

	setAccessToken(token, persistent = false) {
		getStorage(persistent).setItem(STORAGE_KEYS.ACCESS_TOKEN, token)
	}

	getAccessToken() {
		return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
	}

	setRefreshToken(token, persistent = false) {
		getStorage(persistent).setItem(STORAGE_KEYS.REFRESH_TOKEN, token)
	}

	getRefreshToken() {
		return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
	}

	setUserData(userData, persistent = false) {
		getStorage(persistent).setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData))
	}

	getUserData() {
		const data = localStorage.getItem(STORAGE_KEYS.USER_DATA)
		return data ? JSON.parse(data) : null
	}

	setTokenExpiry(expiryTime, persistent = false) {
		getStorage(persistent).setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString())
	}

	getTokenExpiry() {
		const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY)
		return expiry ? parseInt(expiry, 10) : null
	}

	setRememberedEmail(email) {
		localStorage.setItem(STORAGE_KEYS.REMEMBER_EMAIL, email)
	}

	getRememberedEmail() {
		return localStorage.getItem(STORAGE_KEYS.REMEMBER_EMAIL)
	}

	clearRememberedEmail() {
		localStorage.removeItem(STORAGE_KEYS.REMEMBER_EMAIL)
	}

	isTokenPersistent() {
		return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) !== null
	}

	clearAuthData() {
		Object.values(STORAGE_KEYS).forEach(key => {
			localStorage.removeItem(key)
		})
	}

	// ============================================================================
	// UTILITY METHODS
	// ============================================================================

	getCurrentUser() {
		return this.currentUser || this.getUserData()
	}

	isUserAuthenticated() {
		return this.isAuthenticated && this.isTokenValid()
	}

	/**
	 * Check if user has specific permission
	 *
	 * @param {string} resource - Resource name (e.g., 'users', 'projects')
	 * @param {string} action - Action name (e.g., 'view', 'create', 'edit', 'delete')
	 * @returns {boolean} True if user has permission
	 */
	hasPermission(resource, action) {
		const user = this.getCurrentUser()
		if (!user || !user.role || !user.role.permissions) {
			return false
		}

		const permissions = user.role.permissions

		// Check for wildcard permission (Superadmin/Product Owner)
		if (permissions['*'] && permissions['*'].includes('*')) {
			return true
		}

		// Check specific resource permission
		if (permissions[resource]) {
			return permissions[resource].includes(action) ||
			       permissions[resource].includes('*')
		}

		return false
	}

	/**
	 * Check if user has any of the specified roles
	 *
	 * @param {string|string[]} roles - Role slug(s) to check
	 * @returns {boolean} True if user has any of the roles
	 */
	hasRole(roles) {
		const user = this.getCurrentUser()
		if (!user || !user.role) {
			return false
		}

		const roleArray = Array.isArray(roles) ? roles : [roles]
		return roleArray.includes(user.role.slug)
	}

	// ============================================================================
	// PASSWORD RESET METHODS
	// ============================================================================

	/**
	 * Request password reset email
	 *
	 * @param {string} email - User email address
	 * @returns {Promise<Object>} Response from server
	 * @throws {Error} If request fails
	 */
	async forgotPassword(email) {
		try {
			const sanitizedEmail = email.toLowerCase().trim()

			const response = await apiClient.postForm('/auth/forgot-password', {
				email: sanitizedEmail
			})

			return response
		} catch (error) {
			console.error('Forgot password error:', error.message)
			throw this.handleAuthError(error)
		}
	}

	/**
	 * Verify password reset token
	 *
	 * @param {string} token - Password reset token
	 * @returns {Promise<Object>} Response with masked email
	 * @throws {Error} If token is invalid or expired
	 */
	async verifyResetToken(token) {
		try {
			const response = await apiClient.postForm('/auth/verify-reset-token', {
				token: token
			})

			if (!response.success) {
				throw new Error(response.message || 'Invalid or expired token')
			}

			return response
		} catch (error) {
			console.error('Verify reset token error:', error.message)
			throw this.handleAuthError(error)
		}
	}

	/**
	 * Reset password with token
	 *
	 * @param {string} token - Password reset token
	 * @param {string} password - New password
	 * @param {string} passwordConfirmation - Password confirmation
	 * @returns {Promise<Object>} Response from server
	 * @throws {Error} If reset fails
	 */
	async resetPassword(token, password, passwordConfirmation) {
		try {
			const response = await apiClient.postForm('/auth/reset-password', {
				token: token,
				password: password,
				password_confirmation: passwordConfirmation
			})

			if (!response.success) {
				throw new Error(response.message || 'Password reset failed')
			}

			return response
		} catch (error) {
			console.error('Reset password error:', error.message)
			throw this.handleAuthError(error)
		}
	}

	/**
	 * Handle authentication errors
	 */
	handleAuthError(error) {
		// Map backend error codes to user-friendly messages
		const errorMessages = {
			400: error.response?.data?.message || 'Invalid request',
			401: error.response?.data?.message || 'Invalid email or password',
			403: error.response?.data?.message || 'Account is inactive',
			429: error.response?.data?.message || 'Too many login attempts. Please try again later.',
			500: 'Server error. Please try again later.',
			default: error.message || 'An error occurred. Please try again.'
		}

		const status = error.response?.status
		const message = errorMessages[status] || errorMessages.default

		// Preserve additional data from backend (like remaining_attempts, locked_until)
		const newError = new Error(message)
		if (error.response?.data) {
			newError.data = error.response.data
		}

		return newError
	}
}

// Export singleton instance
const authService = new AuthService()
export default authService
